// TanStack Query hooks for episode resources.
//
// Conventions:
//   - Query keys are stable arrays starting with `['episodes', projectId]`.
//   - Mutations invalidate at the narrowest level safe for the operation.
//   - `presigned` flow: caller invokes `useRequestEpisodeUpload`, PUTs the
//     bytes to R2 directly, then invokes `useCompleteEpisodeUpload`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { EpisodeSummary, EpisodeUploads } from './producer-types'

export type EpisodeFileSlot = 'video' | 'ref_audio' | 'script' | 'srt'

export interface PresignedUpload {
  /** Direct R2 PUT URL with embedded signature. Expires in ~1h. */
  put_url: string
  /** Object key the server stored — pass back into `complete`. */
  object_key: string
  /** Required Content-Type the PUT must use. */
  content_type: string
  /** Max bytes accepted (server-enforced). */
  max_bytes: number
}

export interface CreateEpisodeInput {
  project_id: string
  episode_number: number
  title: string
  /** e.g. 23.976, 24, 25, 29.97. */
  frame_rate_fps: number
  drop_frame: boolean
}

export interface ProcessEpisodeOutput {
  /** Modal job id, surfaced in realtime updates. */
  job_id: string
  episode: EpisodeSummary
}

const episodesKey = (projectId: string) => ['episodes', projectId] as const
const episodeKey = (episodeId: string) => ['episode', episodeId] as const

/** List all episodes belonging to a project. */
export function useEpisodes(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? episodesKey(projectId) : ['episodes', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () =>
      api<ListEnvelope<EpisodeSummary>>(`/api/v1/episodes?project_id=${projectId}`).then(
        unwrapItems,
      ),
  })
}

/** Single episode detail. */
export function useEpisode(episodeId: string | undefined) {
  return useQuery({
    queryKey: episodeId ? episodeKey(episodeId) : ['episode', '__none__'],
    enabled: Boolean(episodeId),
    queryFn: () => api<EpisodeSummary>(`/api/v1/episodes/${episodeId}`),
  })
}

/** Create a new episode row (Step 2 of the new-project wizard). */
export function useCreateEpisode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateEpisodeInput) =>
      api<EpisodeSummary>('/api/v1/episodes', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (episode) => {
      void qc.invalidateQueries({ queryKey: episodesKey(episode.project_id) })
    },
  })
}

/** Step 1 of the upload flow: ask the API for a pre-signed PUT URL. */
export function useRequestEpisodeUpload() {
  return useMutation({
    mutationFn: ({
      episodeId,
      slot,
      filename,
      byteSize,
    }: {
      episodeId: string
      slot: EpisodeFileSlot
      filename: string
      byteSize: number
    }) =>
      api<PresignedUpload>(`/api/v1/episodes/${episodeId}/upload-${slot}`, {
        method: 'POST',
        body: JSON.stringify({ filename, byte_size: byteSize }),
      }),
  })
}

/**
 * Step 2 of the upload flow: do the actual PUT. Surfaces an `onProgress`
 * callback for the wizard's checkmark / percent display. Uses XHR (not fetch)
 * because fetch lacks an upload progress event in browsers.
 */
export function uploadToPresigned(
  presigned: PresignedUpload,
  blob: Blob,
  signal: AbortSignal | undefined,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', presigned.put_url, true)
    xhr.setRequestHeader('Content-Type', presigned.content_type)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total)
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`R2 PUT failed (${xhr.status}): ${xhr.responseText}`))
    }
    xhr.onerror = () => reject(new Error('R2 PUT network error'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    if (signal) {
      if (signal.aborted) {
        xhr.abort()
        return
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }
    xhr.send(blob)
  })
}

/** Step 3 of the upload flow: tell the API the bytes are in R2. */
export function useCompleteEpisodeUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      episodeId,
      slot,
      objectKey,
    }: {
      episodeId: string
      slot: EpisodeFileSlot
      objectKey: string
    }) =>
      api<EpisodeUploads>(`/api/v1/episodes/${episodeId}/upload-${slot}/complete`, {
        method: 'POST',
        body: JSON.stringify({ object_key: objectKey }),
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: episodeKey(vars.episodeId) })
    },
  })
}

/** Kick off the Modal processing pipeline once all four files are uploaded. */
export function useProcessEpisode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (episodeId: string) =>
      api<ProcessEpisodeOutput>(`/api/v1/episodes/${episodeId}/process`, {
        method: 'POST',
      }),
    onSuccess: (out) => {
      void qc.invalidateQueries({ queryKey: episodeKey(out.episode.id) })
      void qc.invalidateQueries({ queryKey: episodesKey(out.episode.project_id) })
    },
  })
}
